/**
 * BO VIP Kulüp -- Shared type definitions and constants
 */

// Program identity
export const PROGRAM_NAME = "BO VIP Kulüp";
export const PROGRAM_SUBTITLE = "Büyüklere Oyuncaklar Sadakat Programı";
export const POINTS_UNIT = "Hobi Puan";
export const POINTS_UNIT_SHORT = "HP";

// Tier levels
export const TIERS = {
  ROOKIE: "rookie",
  PRO: "pro",
  LEGEND: "legend",
};

// Legacy tier mapping (for migration)
export const LEGACY_TIER_MAP = {
  bronze: TIERS.ROOKIE,
  silver: TIERS.PRO,
  gold: TIERS.LEGEND,
};

// Tier display names (Turkish)
export const TIER_NAMES = {
  [TIERS.ROOKIE]: "Rookie",
  [TIERS.PRO]: "Pro",
  [TIERS.LEGEND]: "Legend",
};

// Tier emojis
export const TIER_EMOJIS = {
  [TIERS.ROOKIE]: "🎮",
  [TIERS.PRO]: "🏎️",
  [TIERS.LEGEND]: "👑",
};

// Tier colors for UI
export const TIER_COLORS = {
  [TIERS.ROOKIE]: "#3330E4",
  [TIERS.PRO]: "#dbff00",
  [TIERS.LEGEND]: "#ff580d",
};

// Default program settings (overridden by metaobject values)
export const DEFAULT_SETTINGS = {
  // Points earned per 100 TL spent, by tier
  pointsPerHundredTL: {
    [TIERS.ROOKIE]: 3,
    [TIERS.PRO]: 5,
    [TIERS.LEGEND]: 7,
  },

  // Tier thresholds (total spend in TL)
  tierThresholds: {
    [TIERS.ROOKIE]: 0,
    [TIERS.PRO]: 25000,
    [TIERS.LEGEND]: 75000,
  },

  // Coupon tiers (points required -> discount in TL)
  couponTiers: [
    { points: 100, discountTL: 50 },
    { points: 300, discountTL: 150 },
    { points: 800, discountTL: 400 },
    { points: 1500, discountTL: 750 },
  ],

  // Bonus points
  bonusPoints: {
    registration: 50,
    firstOrder: 50,
    birthday: 30,
    profileComplete: 25,
    birthdayEntry: 25,
  },

  // Streak multipliers (consecutive months -> points multiplier)
  streakMultipliers: {
    2: 1.5,  // 2 ardışık ay = 1.5x
    3: 2.0,  // 3+ ardışık ay = 2x
  },
};

// Metafield namespace
export const METAFIELD_NAMESPACE = "boloyalty";

// Shop-level metafield keys
export const SHOP_METAFIELD_KEYS = {
  PROGRAM_ENABLED: "program_enabled", // "true" / "false"
};

// Customer metafield keys
export const METAFIELD_KEYS = {
  POINTS_BALANCE: "points_balance",
  TOTAL_POINTS_EARNED: "total_points_earned",
  TOTAL_SPENT: "total_spent",
  TIER: "tier",
  BADGES: "badges",
  BIRTHDAY: "birthday",
  JOINED_AT: "joined_at",

  // Mini Games (Faz B)
  SPIN_LAST_DATE: "spin_last_date",
  SPIN_STREAK: "spin_streak",
  CHECKIN_LAST_DATE: "checkin_last_date",
  CHECKIN_STREAK: "checkin_streak",
  QUIZ_COMPLETED_WEEK: "quiz_completed_week",
  QUIZ_HISTORY: "quiz_history",
  MYSTERY_BOXES_AVAILABLE: "mystery_boxes_available",
  MYSTERY_BOX_LAST_OPENED: "mystery_box_last_opened",
  PROCESSED_ORDERS: "processed_orders",
};

// Mini Games Config
export const GAME_CONFIG = {
  spinWheel: {
    slices: [
      { label: "5 HP", points: 5, weight: 25, color: "#3330E4" },
      { label: "10 HP", points: 10, weight: 25, color: "#dbff00" },
      { label: "25 HP", points: 25, weight: 20, color: "#ff580d" },
      { label: "50 HP", points: 50, weight: 10, color: "#3330E4" },
      { label: "100 HP", points: 100, weight: 3, color: "#dbff00" },
      { label: "Ekstra Çevirme", points: 0, weight: 7, extra_spin: true, color: "#ff580d" },
      { label: "Boş", points: 0, weight: 10, color: "#2a2b2a" },
    ],
  },
  checkin: {
    dailyPoints: 2,
    weeklyBonusPoints: 25,   // 7 gün streak
    monthlyBonusPoints: 100, // 30 gün streak
    weeklyStreakDays: 7,
    monthlyStreakDays: 30,
  },
  quiz: {
    pointsPerCorrect: 5,
    perfectScoreBonus: 50,   // 5/5 doğru
    questionsPerWeek: 5,
  },
  mysteryBox: {
    pointsPerBox: 200,       // Her 200 HP'de 1 kutu
    rewards: [
      { type: "points", value: 10, weight: 25, rarity: "common", label: "10 HP" },
      { type: "points", value: 25, weight: 20, rarity: "common", label: "25 HP" },
      { type: "points", value: 50, weight: 15, rarity: "rare", label: "50 HP" },
      { type: "points", value: 100, weight: 5, rarity: "rare", label: "100 HP" },
      { type: "coupon", value: 50, weight: 15, rarity: "common", label: "50 TL İndirim" },
      { type: "coupon", value: 150, weight: 8, rarity: "rare", label: "150 TL İndirim" },
      { type: "points", value: 250, weight: 2, rarity: "legendary", label: "250 HP Jackpot!" },
    ],
  },
};

// Badge condition types
export const BADGE_CONDITIONS = {
  ORDER_COUNT: "order_count",
  TOTAL_SPENT: "total_spent",
  CATEGORY_COUNT: "category_count",
  TIER_REACHED: "tier_reached",
};

// Challenge rule types
export const CHALLENGE_TYPES = {
  TIME_BASED: "time_based",
  CATEGORY_BASED: "category_based",
  COMBINED: "combined",
};

// Challenge bonus types
export const BONUS_TYPES = {
  EXTRA_POINTS: "extra_points",
  MULTIPLIER: "multiplier",
};

/**
 * Resolve tier value, mapping legacy names if needed
 */
export function resolveTier(tierValue) {
  if (LEGACY_TIER_MAP[tierValue]) return LEGACY_TIER_MAP[tierValue];
  if (Object.values(TIERS).includes(tierValue)) return tierValue;
  return TIERS.ROOKIE;
}

/**
 * Calculate tier based on total spend
 */
export function calculateTier(totalSpent, thresholds = DEFAULT_SETTINGS.tierThresholds) {
  if (totalSpent >= thresholds[TIERS.LEGEND]) return TIERS.LEGEND;
  if (totalSpent >= thresholds[TIERS.PRO]) return TIERS.PRO;
  return TIERS.ROOKIE;
}

/**
 * Calculate points for an order amount
 */
export function calculatePoints(orderAmountTL, tier, rates = DEFAULT_SETTINGS.pointsPerHundredTL) {
  const resolved = resolveTier(tier);
  const rate = rates[resolved] || rates[TIERS.ROOKIE];
  return Math.floor(orderAmountTL / 100) * rate;
}

/**
 * Get next tier info
 */
export function getNextTierInfo(currentTier, totalSpent, thresholds = DEFAULT_SETTINGS.tierThresholds) {
  const resolved = resolveTier(currentTier);
  if (resolved === TIERS.LEGEND) {
    return null; // Already at max tier
  }

  const nextTier = resolved === TIERS.ROOKIE ? TIERS.PRO : TIERS.LEGEND;
  const threshold = thresholds[nextTier];
  const remaining = threshold - totalSpent;

  return {
    nextTier,
    nextTierName: TIER_NAMES[nextTier],
    threshold,
    remaining: Math.max(0, remaining),
    progress: Math.min(100, (totalSpent / threshold) * 100),
  };
}

/**
 * Get available coupons for a point balance
 */
export function getAvailableCoupons(pointsBalance, couponTiers = DEFAULT_SETTINGS.couponTiers) {
  return couponTiers.filter((c) => pointsBalance >= c.points);
}

/**
 * Calculate retroactive points for existing customers
 */
export function calculateRetroactivePoints(totalSpent, settings = DEFAULT_SETTINGS) {
  // Always use Rookie rate for retroactive calculation
  const rookieRate = settings.pointsPerHundredTL[TIERS.ROOKIE];
  const points = Math.floor(totalSpent / 100) * rookieRate;
  const tier = calculateTier(totalSpent, settings.tierThresholds);

  return { points, tier };
}

// Badge definitions -- RC Hobi Temalı
export const DEFAULT_BADGES = [
  { id: "ilk_pit_stop", name: "İlk Pit Stop", icon: "🏁", conditionType: "order_count", conditionValue: 1, rarity: "common", description: "Hoş geldin, yarış başladı!" },
  { id: "garaj_ustasi", name: "Garaj Ustası", icon: "🔧", conditionType: "order_count", conditionValue: 5, rarity: "common", description: "Garajın dolmaya başladı" },
  { id: "koleksiyoncu", name: "Koleksiyoncu", icon: "🏆", conditionType: "category_count", conditionValue: 3, rarity: "rare", description: "3+ farklı kategoriden alışveriş" },
  { id: "hiz_canavari", name: "Hız Canavarı", icon: "⚡", conditionType: "monthly_order_count", conditionValue: 3, rarity: "rare", description: "Aynı ay 3+ sipariş" },
  { id: "full_gaz", name: "Full Gaz", icon: "🔥", conditionType: "single_order_amount", conditionValue: 1500, rarity: "rare", description: "1.500 TL+ tek sipariş" },
  { id: "sadik_pilot", name: "Sadık Pilot", icon: "⭐", conditionType: "streak_months", conditionValue: 3, rarity: "rare", description: "3 ardışık ay sipariş" },
  { id: "rc_araba_tutkunu", name: "RC Araba Tutkunu", icon: "🏎️", conditionType: "category_count", conditionValue: 3, rarity: "common", description: "RC Araba kategorisinden 3+ ürün" },
  { id: "gokyuzu_hakimi", name: "Gökyüzü Hakimi", icon: "✈️", conditionType: "category_count", conditionValue: 2, rarity: "common", description: "RC Uçak kategorisinden 2+ ürün" },
  { id: "deniz_kurdu", name: "Deniz Kurdu", icon: "🚤", conditionType: "category_count", conditionValue: 2, rarity: "common", description: "RC Tekne kategorisinden 2+ ürün" },
  { id: "efsane_pilot", name: "Efsane Pilot", icon: "👑", conditionType: "tier_reached", conditionValue: "legend", rarity: "legendary", description: "Legend tier'a ulaş" },
  { id: "erken_kus", name: "Erken Kuş", icon: "🐦", conditionType: "early_adopter", conditionValue: 100, rarity: "legendary", description: "Programa ilk 100 kayıt" },
  { id: "dogum_gunu", name: "Doğum Günü", icon: "🎂", conditionType: "birthday_order", conditionValue: 1, rarity: "rare", description: "Doğum gününde sipariş" },
];

// Badge rarity colors
export const BADGE_RARITY = {
  common: { label: "Yaygın", borderColor: "#ffffff" },
  rare: { label: "Nadir", borderColor: "#3330E4" },
  legendary: { label: "Efsanevi", borderColor: "#ff580d" },
};
